import { useState, useCallback } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { Stage, Mission, SiteType } from '../../openapi'
import TrashboxIcon from '../../pages/trashboxIcon'
import { ShareIcon } from '../icons/shareIcon'
import { SimpleModal } from '../modals/simpleModal'
import { ProjectInfo, deleteProject, updateProjectInfo } from '../project/api'
import { buildProjectQuery } from '../project/projectQuery'
import { useTranslator } from '../translator/hook'
import errorIcon from '../icons/errorIcon.svg'
import { getProjectThumbnail } from './projectThumbnail'
import './projectCard.css'

type ProjectCardProps = {
  onChanged: () => void
  onDeleted: (projectId: string) => void
  onError: (error: Error) => void
  onStopSharing: (projectId: string) => void
  project: ProjectInfo
  siteName: SiteType | undefined
  stage?: Stage
  mission?: Mission
  userId: string
}

export const ProjectCard = ({
  onChanged,
  onDeleted,
  onError,
  onStopSharing,
  project,
  siteName,
  stage,
  mission,
  userId,
}: ProjectCardProps) => {
  const { t } = useTranslator()
  const [searchParams] = useSearchParams()
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false)
  const [confirmStopSharingOpen, setConfirmStopSharingOpen] = useState(false)

  const handleDelete = useCallback(async () => {
    setConfirmDeleteOpen(false)
    onDeleted(project.projectId)
    try {
      if (!(await deleteProject(project.projectId))) {
        console.error('Failed to delete project')
        onError(new Error(t('delete_project_error')))
      }
    } catch (err) {
      console.error('Failed to delete project:', err)
      onError(new Error(t('delete_project_error')))
    }
    onChanged()
  }, [onChanged, onDeleted, onError, project.projectId, t])

  const handleStopSharing = useCallback(async () => {
    setConfirmStopSharingOpen(false)
    onStopSharing(project.projectId)
    try {
      const results = await updateProjectInfo(project.projectId, {
        isPublic: false,
        userId,
      })
      if (results?.isPublic) {
        console.error('Failed to update project share status:', results)
        onError(new Error(t('project_update_error')))
      }
    } catch (e) {
      console.error('Error updating project share status:', e)
      onError(new Error(t('project_update_error')))
    }
    onChanged()
  }, [onChanged, onError, onStopSharing, project.projectId, t, userId])

  const thumbnail = getProjectThumbnail(project.isCopy, mission, siteName)

  return (
    <li className="project">
      {/* buildProjectQuery は '?projectId=...' 形式を返すため、/list からの
          相対遷移にならないよう pathname '/' を明示する。 */}
      <Link
        to={{
          pathname: '/',
          search: buildProjectQuery(project.projectId, searchParams),
        }}
      >
        <div className="project-card">
          <img src={thumbnail} alt="thumbnail" className="thumbnail" />
          <div className="project-info">
            <p className="project-detail">
              {mission && !project.isCopy ?
                `${t('stage_label')}${stage?.seq} ${t('mission_label')}${mission.num}「${mission.title}」`
              : ''}
            </p>
            <span className="project-title">{project.title}</span>
            <p className="update-at">
              {t('updated_at_label')}
              {project.updatedAt}
            </p>
          </div>
        </div>
      </Link>
      {project.isPublic && (
        <div
          className="stop-share-button-container"
          onClick={() => setConfirmStopSharingOpen(true)}
        >
          <ShareIcon />
        </div>
      )}
      <TrashboxIcon
        className="delete-button"
        onClick={() => setConfirmDeleteOpen(true)}
      />
      <SimpleModal
        imgUrl={errorIcon}
        imgW={57}
        isOpen={confirmDeleteOpen}
        modalTitle={t('confirm_delete_project')}
        buttonLabel={t('delete_button')}
        onClick={handleDelete}
        closeButtonLabel={t('cancel_button')}
        onClose={() => setConfirmDeleteOpen(false)}
      />
      <SimpleModal
        imgUrl={errorIcon}
        imgW={57}
        isOpen={confirmStopSharingOpen}
        modalTitle={t('confirm_stop_sharing')}
        buttonLabel={t('stop_sharing_button')}
        onClick={handleStopSharing}
        closeButtonLabel={t('cancel_button')}
        onClose={() => setConfirmStopSharingOpen(false)}
      />
    </li>
  )
}
